import { useEffect } from 'react';

import { fetchSiteUserTraits, fetchUserTraits, findTraitValue } from '../tap-service';
import { evaluateRovoTargetingAudience, type RovoTargetingEvaluation } from './eligibility';
import { buildRovoTargetingAudienceEvent } from './rovoTargetingAudienceEvent';
import { useAudienceSnapshotActions } from './rovoTargetingAudienceStore';
import {
	isRovoTargetingProductKey,
	type AudienceScope,
	type AudienceSnapshot,
	type RovoTargetingOperationalEventHandler,
	type RovoTargetingProductKey,
} from './types';

const REQUEST_TIMEOUT_MS = 3000;
const CONFLUENCE_PROFILE = {
	usageTrait: 'ai_chat_confluence_active_days_28d',
	activityTrait: 'confluence_last_active',
} as const;
const TARGETING_PROFILES: Record<
	RovoTargetingProductKey,
	{ usageTrait: string; activityTrait: string }
> = {
	confluence: CONFLUENCE_PROFILE,
	jira: {
		usageTrait: 'ai_chat_jira_active_days_28d',
		activityTrait: 'jira_last_active',
	},
	jsm: {
		usageTrait: 'ai_chat_jsm_active_days_28d',
		activityTrait: 'jira-servicedesk_last_active',
	},
} as const;

type TargetingProfile = (typeof TARGETING_PROFILES)[keyof typeof TARGETING_PROFILES];

const getTargetingProfile = (productKey: string): TargetingProfile | undefined => {
	if (isRovoTargetingProductKey(productKey)) {
		return TARGETING_PROFILES[productKey];
	}
	return undefined;
};

type Entry = {
	owners: Set<RovoTargetingOperationalEventHandler>;
	snapshot: AudienceSnapshot;
	settled: boolean;
	cancel?: () => void;
};

// Populated only by client effects. Completed signals survive hidden/visible Chat remounts,
// but inactive scopes are discarded when a different identity, site or gate state initializes.
const entries = new Map<string, Entry>();

export const getAudienceScopeKey = (scope: AudienceScope): string =>
	JSON.stringify([
		scope.productKey,
		scope.accountId ?? '',
		scope.siteId ?? '',
		scope.hasRovoAccess,
		scope.gateEnabled,
	]);

const emitSafely = (
	entry: Entry,
	evaluation: RovoTargetingEvaluation,
	rollout: 'enabled' | 'disabled',
) => {
	const handler = entry.owners.values().next().value;
	try {
		handler?.(buildRovoTargetingAudienceEvent({ ...evaluation, rollout }));
	} catch {
		// Analytics must not change eligibility or trigger another completion event.
	}
};

type SnapshotActions = ReturnType<typeof useAudienceSnapshotActions>;

const startRequests = (
	entry: Entry,
	accountId: string,
	siteId: string,
	profile: TargetingProfile,
	actions: SnapshotActions,
) => {
	const controller = new AbortController();
	const startedAt = Date.now();
	const scopeKey = entry.snapshot.scopeKey;
	let cancelled = false;
	let usageSettled = false;
	let activitySettled = false;
	let timeoutId: ReturnType<typeof setTimeout> | undefined;

	const finish = (snapshot: AudienceSnapshot, evaluation: RovoTargetingEvaluation) => {
		if (cancelled || entry.settled || entries.get(scopeKey) !== entry) {
			return;
		}
		entry.settled = true;
		entry.snapshot = snapshot;
		entry.cancel = undefined;
		clearTimeout(timeoutId);
		actions.setSnapshot(snapshot);
		emitSafely(entry, { ...evaluation, durationMs: Date.now() - startedAt }, 'enabled');
	};

	entry.cancel = () => {
		cancelled = true;
		clearTimeout(timeoutId);
		controller.abort();
	};
	timeoutId = setTimeout(() => {
		const signal =
			!usageSettled && !activitySettled ? 'both' : !usageSettled ? 'usage' : 'siteActivity';
		finish(
			{ scopeKey, phase: 'unavailable' },
			{ outcome: 'unavailable', signal, reason: 'timeout' },
		);
		controller.abort();
	}, REQUEST_TIMEOUT_MS);

	const usageRequest = fetchUserTraits(accountId, controller.signal).finally(() => {
		usageSettled = true;
	});
	const activityRequest = fetchSiteUserTraits(siteId, accountId, controller.signal).finally(() => {
		activitySettled = true;
	});

	void Promise.allSettled([usageRequest, activityRequest]).then(([usage, activity]) => {
		if (cancelled || entry.settled) {
			return;
		}
		if (usage.status === 'rejected' || activity.status === 'rejected') {
			const failedUsage = usage.status === 'rejected';
			const failedActivity = activity.status === 'rejected';
			const signal =
				failedUsage && failedActivity ? 'both' : failedUsage ? 'usage' : 'siteActivity';
			finish(
				{ scopeKey, phase: 'unavailable' },
				{ outcome: 'unavailable', signal, reason: 'requestFailure' },
			);
			return;
		}
		const activeDays = findTraitValue(usage.value, profile.usageTrait);
		const lastActive = findTraitValue(activity.value, profile.activityTrait);
		const evaluatedAt = Date.now();
		finish(
			{ scopeKey, phase: 'ready', activeDays, lastActive, evaluatedAt },
			evaluateRovoTargetingAudience(activeDays, lastActive, 2, 3, evaluatedAt),
		);
	});
};

export const useRovoTargetingAudienceAcquisition = (
	scope: AudienceScope,
	emit: RovoTargetingOperationalEventHandler,
): void => {
	const scopeKey = getAudienceScopeKey(scope);
	const { setSnapshot, clearSnapshot } = useAudienceSnapshotActions();

	useEffect(() => {
		const profile = getTargetingProfile(scope.productKey);
		for (const [otherKey, previous] of entries) {
			if (otherKey !== scopeKey && previous.owners.size === 0) {
				previous.cancel?.();
				entries.delete(otherKey);
				clearSnapshot(otherKey);
			}
		}
		// Unsupported products and missing host access/identity are not rollout exclusions.
		if (!profile || !scope.hasRovoAccess || !scope.accountId || !scope.siteId) {
			return;
		}
		// Every mount has its own lease, even when mounts share an analytics callback.
		const owner: RovoTargetingOperationalEventHandler = (event) => emit(event);
		let entry = entries.get(scopeKey);
		if (entry) {
			entry.owners.add(owner);
			setSnapshot(entry.snapshot);
		} else {
			entry = {
				owners: new Set([owner]),
				snapshot: { scopeKey, phase: scope.gateEnabled ? 'loading' : 'outsideRollout' },
				settled: !scope.gateEnabled,
			};
			entries.set(scopeKey, entry);
			setSnapshot(entry.snapshot);
			if (scope.gateEnabled) {
				startRequests(entry, scope.accountId, scope.siteId, profile, {
					setSnapshot,
					clearSnapshot,
				});
			} else {
				emitSafely(
					entry,
					{ outcome: 'outsideRollout', signal: 'none', reason: 'none' },
					'disabled',
				);
			}
		}
		const ownedEntry = entry;
		return () => {
			ownedEntry.owners.delete(owner);
			// StrictMode and sibling remounts can reacquire the same request before cleanup.
			void Promise.resolve().then(() => {
				if (
					ownedEntry.owners.size === 0 &&
					!ownedEntry.settled &&
					entries.get(scopeKey) === ownedEntry
				) {
					ownedEntry.cancel?.();
					entries.delete(scopeKey);
					clearSnapshot(scopeKey);
				}
			});
		};
	}, [
		clearSnapshot,
		emit,
		scope.accountId,
		scope.gateEnabled,
		scope.hasRovoAccess,
		scope.productKey,
		scope.siteId,
		scopeKey,
		setSnapshot,
	]);
};
